import { ContactModal, PageMainContent, Typography } from '@/components'
import { Breakpoint, ThemeColor } from '@/enums'
import {
  Alert,
  ArrowRight,
  Document,
  DocumentOverview,
  Flag,
  Logo,
  Message,
  Mobile,
  ShieldSmall,
} from '@/icons'
import { useLocationContext } from '@/providers'
import { paths } from '@/routes/paths'
import {
  makeUrlWithLanguage,
  openHubspotChat,
  useIsMobile,
  useLanguage,
  useModal,
} from '@/utils'
import { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../../config'
import { Container, Details, MenuItemWrapper, SubsectionsTitle } from './styles'

type Language = 'en' | 'de'

interface AboutUsItem {
  title: string
  url: string
  icon: ReactElement
}

interface Messages {
  phone: string
  close: string
  yes: string
  title: string
  report: string
  questions: string
  aboutUs: string
  contacts: string
  chat: string
  about: string
  dataSafe: string
  agb: string
  info: string
  impression: string
  cancel: string
  finish: string
  [key: string]: string
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    phone: t('common.phone'),
    close: t('common.close'),
    yes: t('common.yes'),
    title: t('services.title'),
    report: t('services.report'),
    questions: t('services.questions'),
    aboutUs: t('services.aboutUs'),
    contacts: t('services.contacts'),
    chat: t('services.chat'),
    about: t('services.about'),
    dataSafe: t('services.dataSafe'),
    agb: t('services.agb'),
    info: t('services.info'),
    impression: t('services.impression'),
    cancel: t('common.cancel'),
    finish: t('common.finish'),
  }
}

const makeTermsUrl = makeUrlWithLanguage(paths.terms)
const makePolicyUrl = makeUrlWithLanguage(paths.policy)
const makeInitialInformtationUrl = makeUrlWithLanguage(paths.initialInformation)
const makeImprintUrl = makeUrlWithLanguage(paths.imprint)
const makeAboutUsUrl = (language: Language): string =>
  language === 'en'
    ? 'https://www.surein.de/en/en-surein#how-it-works'
    : 'https://www.surein.de/#so-funktioniert-es'

const aboutUs = (language: Language): AboutUsItem[] => [
  { title: 'about', url: makeAboutUsUrl(language), icon: <Logo /> },
  { title: 'dataSafe', url: makePolicyUrl(language), icon: <ShieldSmall /> },
  { title: 'agb', url: makeTermsUrl(language), icon: <DocumentOverview /> },
  {
    title: 'info',
    url: makeInitialInformtationUrl(language),
    icon: <Document />,
  },
  { title: 'impression', url: makeImprintUrl(language), icon: <Flag /> },
]

export const Services = (): ReactElement => {
  const { clearLocation } = useLocationContext()
  const messages = useMessages()
  const { isOpen, close, toggle } = useModal(false)
  const language = useLanguage()
  const { isMobile } = useIsMobile(Breakpoint.sm)

  useEffect(() => {
    clearLocation()
  }, [clearLocation])

  return (
    <PageMainContent>
      <Container>
        <ContactModal isOpen={isOpen} onClose={close} />
        <SubsectionsTitle>
          <Typography variant="h5">{messages.title} </Typography>
        </SubsectionsTitle>
        <a
          href={config.reportDamageUrl[language]}
          rel="noreferrer"
          target="_blank"
        >
          <MenuItemWrapper>
            <Details>
              <Alert />
              <Typography variant="p1Body">{messages.report} </Typography>
            </Details>
            {isMobile && <ArrowRight />}
          </MenuItemWrapper>
        </a>
        <SubsectionsTitle>
          <Typography variant="h5">{messages.questions} </Typography>
        </SubsectionsTitle>{' '}
        <MenuItemWrapper onClick={toggle}>
          <Details>
            <Mobile />
            <Typography variant="p1Body">{messages.contacts} </Typography>
          </Details>
          {isMobile && <ArrowRight />}
        </MenuItemWrapper>
        <MenuItemWrapper onClick={openHubspotChat}>
          <Details>
            <Message color={ThemeColor.b100} />
            <Typography variant="p1Body">{messages.chat} </Typography>
          </Details>
          {isMobile && <ArrowRight />}
        </MenuItemWrapper>
        <SubsectionsTitle>
          <Typography variant="h5">{messages.aboutUs} </Typography>
        </SubsectionsTitle>
        {aboutUs(language).map((el) => (
          <a key={el.title} href={el.url} rel="noreferrer" target="_blank">
            <MenuItemWrapper>
              <Details>
                {el.icon}
                <Typography variant="p1Body">{messages[el.title]} </Typography>
              </Details>
              {isMobile && <ArrowRight />}
            </MenuItemWrapper>
          </a>
        ))}
      </Container>
    </PageMainContent>
  )
}
